/**
 * Type definitions for the Device Updater Wizard
 */

import { DeviceConfig, IMXDeviceInfo } from './imx-types';

// ============================================================================
// Wizard Steps
// ============================================================================

/**
 * Wizard step identifiers matching PRD flow
 */
export enum WizardStep {
  EID_ENTRY = 'eid-entry',
  DEVICE_CONNECTION = 'device-connection',
  BOOTLOADER_TRANSFER = 'bootloader-transfer',
  EASY_INSTALLER_LOADING = 'easy-installer-loading',
  FIRMWARE_FLASHING = 'firmware-flashing',
  FINALIZATION = 'finalization',
}

/**
 * Ordered list of steps
 */
export const WIZARD_STEPS_ORDER: WizardStep[] = [
  WizardStep.EID_ENTRY,
  WizardStep.DEVICE_CONNECTION,
  WizardStep.BOOTLOADER_TRANSFER,
  WizardStep.EASY_INSTALLER_LOADING,
  WizardStep.FIRMWARE_FLASHING,
  WizardStep.FINALIZATION,
];

/**
 * Step state
 */
export enum StepState {
  PENDING = 'pending',
  ACTIVE = 'active',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ERROR = 'error',
}

/**
 * Step metadata
 */
export interface StepMetadata {
  id: WizardStep;
  title: string;
  description: string;
  canSkip: boolean;
  canGoBack: boolean;
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Error codes for specific failure scenarios
 */
export enum ErrorCode {
  // Step 1: EID Entry
  EID_INVALID_FORMAT = 'eid_invalid_format',
  EID_INVALID_CHECKSUM = 'eid_invalid_checksum',
  EID_NOT_FOUND = 'eid_not_found',
  EID_NOT_G5 = 'eid_not_g5',

  // Step 2: Device Connection
  BROWSER_NOT_SUPPORTED = 'browser_not_supported',
  NO_DEVICE_SELECTED = 'no_device_selected',
  DEVICE_NOT_DETECTED = 'device_not_detected',
  WRONG_DEVICE = 'wrong_device',
  DEVICE_OPEN_FAILED = 'device_open_failed',

  // Firmware Fetch (before transfer steps)
  FIRMWARE_FETCH_FAILED = 'firmware_fetch_failed',
  FIRMWARE_FETCH_UNAUTHENTICATED = 'firmware_fetch_unauthenticated',
  FIRMWARE_VERSION_NOT_FOUND = 'firmware_version_not_found',
  FIRMWARE_FILE_URL_FAILED = 'firmware_file_url_failed',

  // Step 3: Bootloader Transfer
  BOOTLOADER_FILE_NOT_FOUND = 'bootloader_file_not_found',
  BOOTLOADER_TRANSFER_FAILED = 'bootloader_transfer_failed',
  BOOTLOADER_DCD_FAILED = 'bootloader_dcd_failed',
  BOOTLOADER_JUMP_FAILED = 'bootloader_jump_failed',

  // Step 4: Easy Installer Loading
  EASY_INSTALLER_FILE_NOT_FOUND = 'easy_installer_file_not_found',
  EASY_INSTALLER_TRANSFER_FAILED = 'easy_installer_transfer_failed',
  DEVICE_NOT_RE_ENUMERATED = 'device_not_re_enumerated',

  // Step 5: Firmware Flashing
  FIRMWARE_FLASH_FAILED = 'firmware_flash_failed',
  FIRMWARE_VERIFICATION_FAILED = 'firmware_verification_failed',
  STORAGE_ERROR = 'storage_error',

  // General
  DEVICE_DISCONNECTED = 'device_disconnected',
  TIMEOUT = 'timeout',
  UNKNOWN_ERROR = 'unknown_error',
}

/**
 * Recovery action types
 */
export enum RecoveryAction {
  RETRY_STEP = 'retry_step',
  START_OVER = 'start_over',
  POWER_CYCLE = 'power_cycle',
  CHECK_CONNECTION = 'check_connection',
  CONTACT_SUPPORT = 'contact_support',
}

/**
 * Recovery instruction
 */
export interface RecoveryInstruction {
  action: RecoveryAction;
  label: string;
  description: string;
  order: number;
}

/**
 * Updater error with context
 */
export class UpdaterError extends Error {
  constructor(
    message: string,
    public code: ErrorCode,
    public step: WizardStep,
    public recoveryActions: RecoveryInstruction[],
    public technicalDetails?: string
  ) {
    super(message);
    this.name = 'UpdaterError';
    Object.setPrototypeOf(this, UpdaterError.prototype);
  }
}

// ============================================================================
// Wizard State
// ============================================================================

/**
 * Device information captured during connection
 */
export interface DeviceInfo {
  vendorId: number;
  productId: number;
  productName: string;
  stage: 'rom' | 'uboot' | 'unknown';
  config: IMXDeviceInfo | DeviceConfig;
}

/**
 * Complete wizard state
 */
export interface WizardState {
  // Current step
  currentStep: WizardStep;

  // Step states
  stepStates: Map<WizardStep, StepState>;

  // User data
  eid: string | null;
  eidValidated: boolean;

  // Device data
  deviceInfo: DeviceInfo | null;
  deviceConnected: boolean;

  // Progress tracking
  currentProgress: number; // 0-100
  currentProgressMessage: string;

  // Error state
  error: UpdaterError | null;

  // Navigation
  canProceed: boolean;
  canGoBack: boolean;

  // Completion
  completed: boolean;
}

/**
 * State action types
 */
export enum StateActionType {
  SET_STEP = 'SET_STEP',
  NEXT_STEP = 'NEXT_STEP',
  PREVIOUS_STEP = 'PREVIOUS_STEP',
  SKIP_TO_STEP = 'SKIP_TO_STEP',
  SET_EID = 'SET_EID',
  VALIDATE_EID = 'VALIDATE_EID',
  SET_DEVICE_INFO = 'SET_DEVICE_INFO',
  SET_DEVICE_CONNECTED = 'SET_DEVICE_CONNECTED',
  SET_PROGRESS = 'SET_PROGRESS',
  SET_STEP_STATE = 'SET_STEP_STATE',
  SET_ERROR = 'SET_ERROR',
  CLEAR_ERROR = 'CLEAR_ERROR',
  RETRY_CURRENT_STEP = 'RETRY_CURRENT_STEP',
  RESET = 'RESET',
  COMPLETE = 'COMPLETE',
}

/**
 * State actions
 */
export type StateAction =
  | { type: StateActionType.SET_STEP; step: WizardStep }
  | { type: StateActionType.NEXT_STEP }
  | { type: StateActionType.PREVIOUS_STEP }
  | { type: StateActionType.SKIP_TO_STEP; step: WizardStep; markCompletedSteps: WizardStep[] }
  | { type: StateActionType.SET_EID; eid: string }
  | { type: StateActionType.VALIDATE_EID; valid: boolean }
  | { type: StateActionType.SET_DEVICE_INFO; deviceInfo: DeviceInfo }
  | { type: StateActionType.SET_DEVICE_CONNECTED; connected: boolean }
  | { type: StateActionType.SET_PROGRESS; percent: number; message?: string }
  | { type: StateActionType.SET_STEP_STATE; step: WizardStep; state: StepState }
  | { type: StateActionType.SET_ERROR; error: UpdaterError }
  | { type: StateActionType.CLEAR_ERROR }
  | { type: StateActionType.RETRY_CURRENT_STEP }
  | { type: StateActionType.RESET }
  | { type: StateActionType.COMPLETE };

// ============================================================================
// Wizard Context
// ============================================================================

/**
 * Wizard context provided to components
 */
export interface WizardContext {
  state: WizardState;
  dispatch: (action: StateAction) => void;

  // Convenience methods
  setEid: (eid: string) => void;
  validateEid: (valid: boolean) => void;
  setDeviceInfo: (info: DeviceInfo) => void;
  setProgress: (percent: number, message?: string) => void;
  setError: (error: UpdaterError) => void;
  clearError: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipToStep: (step: WizardStep, markCompletedSteps: WizardStep[]) => void;
  retry: () => void;
  reset: () => void;
  complete: () => void;
}
